import { useCallback, useEffect, useMemo, useState } from "react";

import { globalEvents } from "../../common/helpers/GlobalEvents";
import { TOAST_EVENTS } from "../../components/Toast/constants";
import { addJWTInConfig } from "../../components/apiHooks/helpers";
import useApiRequestPost from "../../components/apiHooks/useApiRequestPost";
import {
	useCartActions,
	useCartContext,
} from "../../contexts/CartServiceContext/CartServiceContext";
import { useCatalogueActions } from "../../contexts/CatalogueContext/CatalogueContext";
import { useConfigActions, useConfigContext } from "../../contexts/ConfigContext/ConfigContext";
import { usePaymentActions } from "../../contexts/PaymentContext/PaymentContext";
import { addDatadogAction } from "../../services/datadog";
import { ICatalogue } from "../useGetCatalogueData/types";
import useGetSession from "../useGetSession";
import { API_ERROR_MESSAGES, CHECKOUT_ERROR_TYPES, HTTP_STATUS } from "./constants";
import { ICheckoutResponse } from "./types";
import { useFormattedCart } from "./useFormattedCart";

export { CHECKOUT_ERROR_TYPES } from "./constants";

const HOST: string = process.env.CLOUD_RUN_GATEWAY_HOST ?? "";
const END_POINT: string = process.env.API_CHECKOUT ?? "";

const url: string = `${HOST}${END_POINT}`;

interface IOosError {
	detail: string;
	code: string;
	meta?: {
		outofstock_food_supplier_menu_item_id?: number[];
	};
}

interface IApiError {
	response?: {
		data?: {
			errors?: IOosError[];
			message?: string;
			code?: number;
		};
	};
}

export interface ICheckoutError {
	type: typeof CHECKOUT_ERROR_TYPES.OOS_ERROR;
	items: string[];
}

const isJwtExpiredError = (error: unknown): boolean => {
	const apiError = error as IApiError;
	const errorData = apiError?.response?.data;

	return (
		errorData?.message === API_ERROR_MESSAGES.JWT_EXPIRED &&
		errorData?.code === HTTP_STATUS.UNAUTHORIZED
	);
};

const isOutOfStockError = (error: unknown): IOosError | null => {
	const apiError = error as IApiError;
	const errorData = apiError?.response?.data;

	if (!errorData?.errors?.length) return null;

	const oosError = errorData.errors.find(
		(err: IOosError) =>
			err.detail === "oss items" &&
			err.code === "APP_ERROR_NOT_HANDLE" &&
			err.meta?.outofstock_food_supplier_menu_item_id
	);

	return oosError || null;
};

const useSubmitCheckout = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [checkoutError, setCheckoutError] = useState<ICheckoutError | Error | null>(null);
	const [oosItemIds, setOosItemIds] = useState<number[]>([]);
	const [oosItemNames, setOosItemNames] = useState<string[]>([]);
	const {
		state: { is_takeaway, cart, takeaway_byob, selectedGrillNumber },
	} = useCartContext((state) => state);
	const {
		state: { location, user },
	} = useConfigContext((state) => state);
	const paymentActions = usePaymentActions();
	const cartActions = useCartActions();
	const catalogueActions = useCatalogueActions();
	const configActions = useConfigActions();
	const { postData: refreshSession } = useGetSession({ autoFetch: false });
	const formattedCart = useFormattedCart();

	const payload = useMemo(
		() => ({
			url,
			triggerOnMount: false,
			axiosConfig: addJWTInConfig(undefined, user?.session_id?.toString() ?? ""),
			body: {
				bbid: location?.id,
				is_takeaway: is_takeaway,
				byoc: takeaway_byob,
				"extra_info_from_order": {},
				items: formattedCart,
				...(selectedGrillNumber != null && { grill: selectedGrillNumber }),
			},
			swrConfig: {
				revalidateOnFocus: false,
				revalidateOnReconnect: false,
				revalidateIfStale: false,
				shouldRetryOnError: false,
				errorRetryInterval: 0,
				errorRetryCount: 0,
				revalidate: false,
			},
		}),
		[
			location,
			is_takeaway,
			formattedCart,
			url,
			takeaway_byob,
			user?.session_id,
			selectedGrillNumber,
		]
	);

	const { data: response, postData, error } = useApiRequestPost<ICheckoutResponse>(payload);

	// Helper function to check if any customization option is OOS
	const hasOosCustomization = useCallback(
		(customizations: any[] | undefined | null, oosIds: number[]): boolean => {
			if (!customizations) return false;

			return customizations.some((customization) => {
				const options = customization.food_supplier_customization_options;

				if (!options) return false;

				return options.some((option: any) => {
					const optionId = Number(option.id);

					return oosIds.includes(optionId);
				});
			});
		},
		[]
	);

	// Helper function to check if any set item is OOS
	const hasOosSetItem = useCallback(
		(setItems: any[] | undefined | null, oosIds: number[]): boolean => {
			if (!setItems) return false;

			return setItems.some((setItem) => {
				const setItemId = Number(setItem.food_supplier_menu_item_id);

				// Check if set item itself is OOS
				if (oosIds.includes(setItemId)) return true;

				// Check if any customization in set item is OOS
				return hasOosCustomization(setItem.food_supplier_customizations, oosIds);
			});
		},
		[hasOosCustomization]
	);

	// Helper function to check if cart item should be removed
	const isItemOos = useCallback(
		(item: ICatalogue, oosIds: number[]): boolean => {
			const itemId = Number.parseInt(item.id);

			// Check if main item is OOS
			if (oosIds.includes(itemId)) return true;

			// Check if any set item or its customizations are OOS
			if (hasOosSetItem(item.food_supplier_set_items, oosIds)) return true;

			// Check if any customization option in main item is OOS
			return hasOosCustomization(item.food_supplier_customizations, oosIds);
		},
		[hasOosSetItem, hasOosCustomization]
	);

	const removeOosItems = useCallback(() => {
		// Filter out OOS items from cart
		const updatedCart = cart.filter((item) => !isItemOos(item, oosItemIds));

		cartActions.UPDATE_CART(updatedCart);
		setOosItemIds([]);
		setOosItemNames([]);
		setCheckoutError(null);

		// Show toast message
		setTimeout(() => {
			globalEvents.emit(TOAST_EVENTS.SHOW_REMOVE_ITEM_TOAST);
		}, 100);
	}, [cart, cartActions, isItemOos, oosItemIds]);

	const submitCheckout = useCallback(async () => {
		if (formattedCart.length === 0) {
			return;
		}

		setIsLoading(true);
		setCheckoutError(null);
		setOosItemIds([]);
		setOosItemNames([]);

		try {
			// Refresh JWT before checkout. JWT TTL is 1h; POS stays foreground
			// long enough for tokens to lapse mid-session. Force-fetching the
			// session here keeps the original Promise.allSettled reload path
			// (window.location.reload on JWT_EXPIRED) as a residual fallback
			// only, instead of the common case.
			const sessionResp = await refreshSession();
			const freshToken = sessionResp?.data?.token;

			if (!freshToken) {
				throw new Error("Session refresh failed");
			}

			configActions.SET_SESSION({ session_id: freshToken });
			addDatadogAction("checkout_session_refreshed", {});

			// Pass the fresh token via overrideConfig so the request bypasses
			// the stale axiosConfig captured in payload's useMemo closure
			// (SET_SESSION's render hasn't propagated to useMemo yet inside
			// this callback).
			await postData(undefined, addJWTInConfig(undefined, freshToken));
			setIsLoading(false);
		} catch (err) {
			setIsLoading(false);

			// Check if JWT is expired (residual fallback after refresh)
			if (isJwtExpiredError(err)) {
				addDatadogAction("checkout_jwt_expired_after_refresh", {});
				globalThis.location.reload();

				return;
			}

			// Check if it's an OOS error
			const oosError = isOutOfStockError(err);

			if (oosError?.meta?.outofstock_food_supplier_menu_item_id) {
				const oosIds = oosError.meta.outofstock_food_supplier_menu_item_id;

				// Find which cart items will be removed (including items with OOS set items or customizations)
				const itemsToRemove = cart.filter((item) => isItemOos(item, oosIds));

				// Get the names of items that will be removed
				const itemNames = itemsToRemove.map((item) => item.title);

				// Store OOS item IDs and names
				setOosItemIds(oosIds);
				setOosItemNames(itemNames);
				setCheckoutError({ type: CHECKOUT_ERROR_TYPES.OOS_ERROR, items: itemNames });

				// Update product list data to set inventory_count to 0
				catalogueActions.UPDATE_OOS_ITEMS(oosIds);
			} else {
				// Set error for non-OOS errors to show popup
				setCheckoutError(err instanceof Error ? err : new Error("Checkout failed"));
			}
		}
	}, [formattedCart, postData, cart, catalogueActions, isItemOos, refreshSession, configActions]);

	useEffect(() => {
		if (!response) return;

		addDatadogAction("checkout_set_payment_data", {
			hasPaymentReference: !!response?.payment_reference,
		});
		paymentActions.SET_PAYMENT_DATA(response);
	}, [response, paymentActions]);

	return { submitCheckout, isLoading, error: checkoutError, removeOosItems, oosItemNames };
};

export default useSubmitCheckout;
